import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, Subscription, take, tap } from 'rxjs';

import { BulkRemovePermissionResponseDTO } from '../../../../common/dtos/access-permissions.dto';
import { OwnAccountDTO } from '../../../../common/dtos/own-account.dto';
import { ACCOUNT_ID_PARAM } from '../../../constants/application-routes';
import { resetGrantRevokePermissionState } from '../../../store/actions/grant-revoke-permissions.action';
import { updateRemoveUserStepperSelectedAccount } from '../../../store/actions/remove-user-stepper-flow.action';
import { selectAccountById, selectUserInfo } from '../../../store/selectors/account-permissions.selector';
import {
    selectOperationResponse,
    selectRemoveUserStepperSelectedUserName,
    selectSuccessfulAccountsCount,
} from '../../../store/selectors/grant-revoke-permissions.selector';
import { ErrorPage } from '../../../services/generic-error/error-page-utils';
import { ErrorService } from '../../../services/generic-error/error.service';
import { PageComponent } from '../page.component';
import { FlowKey } from '../../../enums/flow-keys.enum';
import { StepperComponent } from '../../stepper/stepper.component';
import { AsyncPipe } from '@angular/common';
import { RemoveUserConfirmationPageComponent } from '../remove-user-confirmation-page/remove-user-confirmation-page.component';
import { OperationResponse } from '../../../interfaces/access-flow-stepper.interface';
import { LoggerService } from '../../../services/logger/logger.service';

@Component({
    selector: 'zci-remove-user-from-account-page',
    templateUrl: './remove-user-from-account-page.component.html',
    styleUrls: ['./remove-user-from-account-page.component.scss'],
    imports: [
        AsyncPipe,
        StepperComponent,
        RemoveUserConfirmationPageComponent,
    ],
})
export class RemoveUserFromAccountPageComponent extends PageComponent implements OnInit, OnDestroy {
    private readonly activatedRoute = inject(ActivatedRoute);
    private store = inject(Store);
    private errorService = inject(ErrorService);
    private readonly loggerService = inject(LoggerService);

    FlowKey = FlowKey;
    accountId: string;

    operationResponse$ = this.store.select(selectOperationResponse);

    private subs = new Subscription();

    constructor() {
        super();
        this.store.dispatch(resetGrantRevokePermissionState());
    }

    ngOnInit(): void {
        this.storeSelectedAccount();
        this.handleOperationResponse();
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    handleOperationResponse(): void {
        this.subs.add(
            combineLatest([
                this.store.select(selectOperationResponse),
                this.store.select(selectSuccessfulAccountsCount),
                this.store.select(selectRemoveUserStepperSelectedUserName),
                this.store.select(selectUserInfo),
            ]).subscribe(([operationResponse, successCount, granteeName, grantorInfo]: [OperationResponse, number, string, any]) => {
                if (!operationResponse) {
                    return;
                }
                const results = (operationResponse as BulkRemovePermissionResponseDTO).results;
                if (!results) {
                    return;
                }
                const failedCount = results.filter((r) => !r.confirmationNumber).length;
                const hasPartialFailure = successCount > 0 && results.some((r) => !r.confirmationNumber);
                if (hasPartialFailure) {
                    this.loggerService.info(
                        `[RemoveUserFromAccountPage] Grantor POID: ${grantorInfo?.clientPoid}, ` +
                        `Navigation to all accounts partial success RAF page, ` +
                        `Total results: ${results.length}, Successful: ${successCount}, Failed: ${failedCount}`,
                    );
                    this.errorService.showError(ErrorPage.RAF_PARTIAL_SUCCESS, { granteeName });
                } else if (successCount === 0) {
                    this.loggerService.info(
                        `[RemoveUserFromAccountPage] Grantor POID: ${grantorInfo?.clientPoid}, ` +
                        `Navigation to all accounts failed RAF page, ` +
                        `Total results: ${results.length}, Successful: ${successCount}, Failed: ${failedCount}`,
                    );
                    this.errorService.showError(ErrorPage.RAF_SUBMIT_ALL_FAILED, { granteeName });
                } else {
                    this.loggerService.info(
                        `[RemoveUserFromAccountPage] Grantor POID: ${grantorInfo?.clientPoid}, Navigating to all accounts success RAF page`,
                    );
                }
            }),
        );
    }

    private storeSelectedAccount() {
        this.store
            .select(selectAccountById(this.accountId))
            .pipe(
                take(1),
                tap((selectedAccount: OwnAccountDTO) => {
                    this.store.dispatch(
                        updateRemoveUserStepperSelectedAccount({
                            selectedAccount: selectedAccount,
                        }),
                    );
                }),
            )
            .subscribe();
    }
}
